import { useCallback, useState } from 'react';
import type { AutomationStep, StepConnection } from '../types/automation';
import type { ValidationResult } from '../utils/flowValidation';
import { getValidationSummary, isFlowValid } from '../utils/flowValidation';

/**
 * Flow save / versioning, extracted from `ListFlowBuilder` (Phase 0 of the
 * builder 2.0 work).
 *
 * ## The host-concern split
 *
 * The 1.0 handlers baked `Toast` and `navigate()` straight into the save
 * logic. That's wrong for reuse: the standalone flow editor wants flow-editor
 * toasts and `/automation/flows/...` navigation, but the campaign builder
 * wants "draft saved" language and campaign-flavoured navigation.
 *
 * So this hook owns ONLY the API orchestration + validation gate + the
 * `saving` flag. Every operation returns a typed {@link FlowPersistenceOutcome};
 * the host decides what toast to show and where (if anywhere) to navigate.
 * That's what lets the flow editor and the campaign builder present the same
 * underlying versioning completely differently.
 */

/** What the minimal API surface the persistence hook needs looks like. */
interface FlowPersistenceApi {
	saveWorkflow(
		flowId: number,
		steps: Array<unknown>,
		connections: Array<unknown>,
		options: {
			versionId: number;
			allowDraftOverwrite: boolean;
			versionDescription: string;
		},
	): Promise<{
		draftVersion?: { id: number } | null;
		steps?: Array<unknown>;
		connections?: Array<unknown>;
	}>;
	commitVersion(
		flowId: number,
		versionId: number,
		payload: {
			steps?: Array<unknown>;
			connections?: Array<unknown>;
		},
	): Promise<{ versionId?: number }>;
	setActiveVersion(flowId: number, versionId: number): Promise<unknown>;
}

interface UseFlowPersistenceParams {
	apiService: FlowPersistenceApi | null | undefined;
	flowId: number;
	selectedVersionId: number | undefined;
	steps: Array<AutomationStep>;
	connections: Array<StepConnection>;
	validationResult: ValidationResult;
	hasUnsavedChanges: boolean;
	/**
	 * The flow object. Mutated in place on save/commit to keep `draftVersion`
	 * / `activeVersionId` current — matching the 1.0 behaviour exactly. Only
	 * the three version-tracking fields are declared; hosts pass richer flow
	 * objects, which structurally satisfy this.
	 */
	flow: {
		draftVersionId?: number;
		draftVersion?: unknown;
		activeVersionId?: number;
	};
}

/**
 * Result of a persistence operation. The host inspects this to decide which
 * toast (if any) to show and where to navigate.
 */
export interface FlowPersistenceOutcome {
	/** True when the operation succeeded. */
	ok: boolean;
	/**
	 * Set when validation blocked the operation. The host turns this into a
	 * "fix N errors" toast.
	 */
	validationError?: { errorCount: number; nodeCount: number };
	/** Generic failure message (guard failure or thrown API error). */
	error?: string;
	/**
	 * `save()` only — the new draft version id, set ONLY when it differs from
	 * the version that was being edited (i.e. a fresh draft was branched off).
	 * The host navigates to it to reload fresh.
	 */
	newDraftVersionId?: number;
	/**
	 * `commitVersion()` / `saveAndCommit()` / `saveAndMakeActive()` — the
	 * resulting committed/active version id.
	 */
	resultVersionId?: number;
}

/** Internal: convert editor steps/connections to the saveWorkflow payload shape. */
function toWorkflowFormat(
	steps: Array<AutomationStep>,
	connections: Array<StepConnection>,
): {
	workflowSteps: Array<unknown>;
	workflowConnections: Array<unknown>;
} {
	const workflowSteps = steps.map((step) => ({
		internalId: step.internalId,
		id: step.id,
		stepType: step.stepType,
		stepName: step.stepName,
		config: step.config,
	}));

	// Map numeric step ID -> UUID internalId for connection endpoints.
	const stepIdToInternalId = new Map<number, string>();
	steps.forEach((step) => {
		if (step.id != null && step.internalId != null) {
			stepIdToInternalId.set(step.id, step.internalId);
		}
	});

	const workflowConnections = connections.map((conn, index) => ({
		internalId: `conn-${index}`,
		id: conn.id,
		fromStepInternalId:
			stepIdToInternalId.get(conn.fromStepId) ?? conn.fromStepId.toString(),
		toStepInternalId:
			conn.toStepId != null
				? (stepIdToInternalId.get(conn.toStepId) ?? conn.toStepId.toString())
				: '',
		conditionKey: conn.conditionKey ?? 'next',
	}));

	return { workflowSteps, workflowConnections };
}

export interface FlowPersistence {
	/** True while any persistence operation is in flight. */
	saving: boolean;
	/** Save the current steps/connections as a draft. */
	save: () => Promise<FlowPersistenceOutcome>;
	/** Commit the currently-selected version. */
	commitVersion: () => Promise<FlowPersistenceOutcome>;
	/** Save a draft then commit it in one operation. */
	saveAndCommit: () => Promise<FlowPersistenceOutcome>;
	/** Save a draft, commit it, then set it as the active version. */
	saveAndMakeActive: () => Promise<FlowPersistenceOutcome>;
	/** Set an already-committed version as active. */
	makeActive: (versionId: number) => Promise<FlowPersistenceOutcome>;
}

export function useFlowPersistence(
	params: UseFlowPersistenceParams,
): FlowPersistence {
	const {
		apiService,
		flowId,
		selectedVersionId,
		steps,
		connections,
		validationResult,
		hasUnsavedChanges,
		flow,
	} = params;

	const [saving, setSaving] = useState(false);

	/** Shared validation gate — returns the outcome to bail with, or null to proceed. */
	const checkValidation = useCallback((): FlowPersistenceOutcome | null => {
		if (!isFlowValid(validationResult)) {
			const summary = getValidationSummary(validationResult);
			return {
				ok: false,
				validationError: {
					errorCount: summary.errorCount,
					nodeCount: summary.nodeCount,
				},
			};
		}
		return null;
	}, [validationResult]);

	const save = useCallback(async (): Promise<FlowPersistenceOutcome> => {
		if (apiService == null) {
			return { ok: false, error: 'API service not initialized' };
		}
		if (selectedVersionId == null) {
			return { ok: false, error: 'No version selected' };
		}

		const validationBail = checkValidation();
		if (validationBail != null) {
			return validationBail;
		}

		try {
			setSaving(true);
			const { workflowSteps, workflowConnections } = toWorkflowFormat(
				steps,
				connections,
			);

			const result = await apiService.saveWorkflow(
				flowId,
				workflowSteps,
				workflowConnections,
				{
					versionId: selectedVersionId,
					allowDraftOverwrite: true,
					versionDescription: 'Draft version',
				},
			);

			// Surface a new draft version id ONLY when it actually changed —
			// that's the host's cue to navigate to it and reload fresh.
			let newDraftVersionId: number | undefined;
			if (
				result.draftVersion != null &&
				result.draftVersion.id !== selectedVersionId
			) {
				newDraftVersionId = result.draftVersion.id;
			}

			return { ok: true, newDraftVersionId };
		} catch (error) {
			return {
				ok: false,
				error: error instanceof Error ? error.message : 'Unknown error',
			};
		} finally {
			setSaving(false);
		}
	}, [
		apiService,
		flowId,
		selectedVersionId,
		steps,
		connections,
		checkValidation,
	]);

	const commitVersion =
		useCallback(async (): Promise<FlowPersistenceOutcome> => {
			if (apiService == null || selectedVersionId == null) {
				return { ok: false, error: 'API service not initialized' };
			}

			const validationBail = checkValidation();
			if (validationBail != null) {
				return validationBail;
			}

			try {
				setSaving(true);
				const result = await apiService.commitVersion(
					flowId,
					selectedVersionId,
					{
						steps: hasUnsavedChanges ? steps : undefined,
						connections: hasUnsavedChanges ? connections : undefined,
					},
				);
				return { ok: true, resultVersionId: result.versionId };
			} catch (error) {
				return {
					ok: false,
					error: error instanceof Error ? error.message : 'Unknown error',
				};
			} finally {
				setSaving(false);
			}
		}, [
			apiService,
			flowId,
			selectedVersionId,
			hasUnsavedChanges,
			steps,
			connections,
			checkValidation,
		]);

	const saveAndCommit =
		useCallback(async (): Promise<FlowPersistenceOutcome> => {
			if (apiService == null || selectedVersionId == null) {
				return { ok: false, error: 'API service not initialized' };
			}

			const validationBail = checkValidation();
			if (validationBail != null) {
				return validationBail;
			}

			try {
				setSaving(true);
				const { workflowSteps, workflowConnections } = toWorkflowFormat(
					steps,
					connections,
				);

				// Save as draft first.
				const saveResult = await apiService.saveWorkflow(
					flowId,
					workflowSteps,
					workflowConnections,
					{
						versionId: selectedVersionId,
						allowDraftOverwrite: true,
						versionDescription: 'Draft version',
					},
				);

				let versionToCommit = selectedVersionId;
				if (saveResult.draftVersion != null) {
					versionToCommit = saveResult.draftVersion.id;
					// Keep the in-memory flow object current — matches 1.0.
					flow.draftVersionId = versionToCommit;
					flow.draftVersion = {
						...saveResult.draftVersion,
						steps: saveResult.steps || [],
						connections: saveResult.connections || [],
					};
				}

				// Then commit the draft.
				const commitResult = await apiService.commitVersion(
					flowId,
					versionToCommit,
					{ steps: undefined, connections: undefined },
				);

				return {
					ok: true,
					resultVersionId: commitResult.versionId ?? versionToCommit,
				};
			} catch (error) {
				return {
					ok: false,
					error: error instanceof Error ? error.message : 'Unknown error',
				};
			} finally {
				setSaving(false);
			}
		}, [
			apiService,
			flowId,
			selectedVersionId,
			steps,
			connections,
			flow,
			checkValidation,
		]);

	const saveAndMakeActive =
		useCallback(async (): Promise<FlowPersistenceOutcome> => {
			if (apiService == null || selectedVersionId == null) {
				return { ok: false, error: 'API service not initialized' };
			}

			const validationBail = checkValidation();
			if (validationBail != null) {
				return validationBail;
			}

			try {
				setSaving(true);
				const { workflowSteps, workflowConnections } = toWorkflowFormat(
					steps,
					connections,
				);

				// Save as draft first.
				const saveResult = await apiService.saveWorkflow(
					flowId,
					workflowSteps,
					workflowConnections,
					{
						versionId: selectedVersionId,
						allowDraftOverwrite: true,
						versionDescription: 'Draft version',
					},
				);

				let versionToActivate = selectedVersionId;
				if (saveResult.draftVersion != null) {
					versionToActivate = saveResult.draftVersion.id;
					flow.draftVersionId = versionToActivate;
					flow.draftVersion = {
						...saveResult.draftVersion,
						steps: saveResult.steps || [],
						connections: saveResult.connections || [],
					};
				}

				// Commit the draft.
				const commitResult = await apiService.commitVersion(
					flowId,
					versionToActivate,
					{ steps: undefined, connections: undefined },
				);
				const committedVersionId = commitResult.versionId ?? versionToActivate;

				// Then make it active.
				await apiService.setActiveVersion(flowId, committedVersionId);

				return { ok: true, resultVersionId: committedVersionId };
			} catch (error) {
				return {
					ok: false,
					error: error instanceof Error ? error.message : 'Unknown error',
				};
			} finally {
				setSaving(false);
			}
		}, [
			apiService,
			flowId,
			selectedVersionId,
			steps,
			connections,
			flow,
			checkValidation,
		]);

	const makeActive = useCallback(
		async (versionId: number): Promise<FlowPersistenceOutcome> => {
			if (apiService == null) {
				return { ok: false, error: 'API service not initialized' };
			}

			try {
				await apiService.setActiveVersion(flowId, versionId);
				// Keep the in-memory flow object current — matches 1.0.
				flow.activeVersionId = versionId;
				return { ok: true, resultVersionId: versionId };
			} catch (error) {
				return {
					ok: false,
					error: error instanceof Error ? error.message : 'Unknown error',
				};
			}
		},
		[apiService, flowId, flow],
	);

	return {
		saving,
		save,
		commitVersion,
		saveAndCommit,
		saveAndMakeActive,
		makeActive,
	};
}
